import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, IconButton, type ModalDialogButtonSetting } from '@myosh/odin-components'
import { useEffect, useMemo, useState } from 'react'
import { type Control, type FieldValue, type FieldValues, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import * as yup from 'yup'
import {
  type ScheduledTrainingReportDto,
  type ScheduledTrainingReportRuleDto,
  useCreateTrainingMatrixReportMutation,
  useTrainingMatrixReportQuery,
  useUpdateTrainingMatrixReportMutation
} from '../../../../API/rtk-query-api/store/tmsApi'
import { useGapAnalysisViews } from '../../../../mixins/useGapAnalysisViews'
import { useTrainingMatrixViews } from '../../../../mixins/useTrainingMatrixViews'
import HFDropdown from '../../../UI/dropdown/HFDropdown'
import HFInputChips from '../../../UI/input-chips/HFInputChips'
import HFInputNumber from '../../../UI/input-number/HFInputNumber'
import Skeleton from '../../../UI/loading-spinner/Skeleton'
import type { DropdownOption } from '../../../UI/multi-select-dropdown/MultiSelectDropdown'
import Notifications from '../../../UI/notifications/Notifications'
import HFSimpleInput from '../../../UI/simple-input/HFSimpleInput'
import { Page } from '../../training-matrix/view_dropdown_data'
import {
  dayOfWeekEnumAsDropdownItems,
  getAllTimezones,
  getValidationErrorMessage,
  modifyReportToForm,
  monthEnumAsDropdownItems,
  type ScheduleRules,
  ScheduleType,
  ScheduleTypeName,
  scheduleTypeAsDropdownItems
} from './scheduledReport-utils'

interface ScheduledExportProps {
  isVisible: boolean
  selectedPage: Page
  id?: string
  hide: () => void
}

const dialogId = 'schedule-export-dialog'
const scheduleExportToasterId = 'schedule-export-toaster'

const defaultValues = {
  reportName: '',
  subject: '',
  timezone: undefined,
  view: undefined,
  rules: [
    {
      type: { text: ScheduleTypeName[ScheduleType.Daily], value: String(ScheduleType.Daily) },
      hour: undefined,
      day: null,
      month: null,
      dayOfWeek: null
    }
  ]
}

const schema = yup.object().shape({
  reportName: yup.string().required(getValidationErrorMessage('Report name')),
  subject: yup.string().required(getValidationErrorMessage('Subject')),
  recipients: yup
    .array()
    .required(getValidationErrorMessage('Recipients Emails'))
    .of(yup.string().email())
    .min(1, getValidationErrorMessage('Recipients Emails')),
  view: yup.object().required(getValidationErrorMessage('View to Export')),
  timezone: yup.object().required(getValidationErrorMessage('Timezone')),
  rules: yup
    .array()
    .required()
    .of(
      yup.object().shape({
        type: yup.object().required(getValidationErrorMessage('Type')),
        month: yup
          .object()
          .when('type', ([type], schema) => {
            return type?.value === String(ScheduleType.Yearly)
              ? schema.required(getValidationErrorMessage('Month'))
              : schema
          })
          .nullable(),
        day: yup
          .number()
          .when('type', ([type], schema) => {
            return type?.value === String(ScheduleType.Yearly) || type?.value === String(ScheduleType.Monthly)
              ? schema.required(getValidationErrorMessage('Day'))
              : schema
          })
          .nullable(),
        dayOfWeek: yup
          .object()
          .when('type', ([type], schema) => {
            return type?.value === String(ScheduleType.Weekly)
              ? schema.required(getValidationErrorMessage('Day of week'))
              : schema
          })
          .nullable(),
        hour: yup.number().required(getValidationErrorMessage('Hour'))
      })
    )
})

function ScheduledExport({ isVisible, id, selectedPage, hide }: ScheduledExportProps) {
  const [recipients, setRecipients] = useState<Array<string>>([])
  const [selectedView, setSelectedView] = useState<DropdownOption>()
  const [selectedTimezone, setSelectedTimezone] = useState<DropdownOption>()
  const [scheduleRules, setScheduleRules] = useState<Array<ScheduleRules>>([
    { id: crypto.randomUUID(), type: ScheduleType.Daily }
  ])

  const {
    views: trainingMatrixViews,
    isFetching: isFetchingTrainingMatrixViews,
    refetch: refetchTrainingMatrixViews
  } = useTrainingMatrixViews(selectedPage === Page.TrainingMatrix)
  const {
    views: gapAnalysisViews,
    isFetching: isFetchingGapAnalysisViews,
    refetch: refetchGapAnalysisViews
  } = useGapAnalysisViews(selectedPage === Page.GapAnalysis)

  const {
    data: report,
    isFetching: isFetchingReport,
    refetch: refetchReports
  } = useTrainingMatrixReportQuery({ id: id ?? '' }, { skip: id === undefined })

  const [createReport] = useCreateTrainingMatrixReportMutation()
  const [updateReport] = useUpdateTrainingMatrixReportMutation()

  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      reportName: '',
      subject: '',
      timezone: undefined,
      view: undefined,
      rules: [
        {
          type: { text: ScheduleTypeName[ScheduleType.Daily], value: String(ScheduleType.Daily) },
          hour: undefined,
          day: null,
          month: null,
          dayOfWeek: null
        }
      ]
    }
  })

  useEffect(() => {
    if (isVisible) {
      if (selectedPage === Page.TrainingMatrix) {
        refetchTrainingMatrixViews()
      } else if (selectedPage === Page.GapAnalysis) {
        refetchGapAnalysisViews()
      }

      if (id) {
        refetchReports()
      }
    }
  }, [isVisible, selectedPage, id, refetchTrainingMatrixViews, refetchGapAnalysisViews, refetchReports])

  useEffect(() => {
    if (!isFetchingReport && report) {
      const reportValues = modifyReportToForm(report)

      setSelectedTimezone(reportValues.timezone)

      if (trainingMatrixViews && gapAnalysisViews) {
        const savedView = trainingMatrixViews.concat(gapAnalysisViews).find(view => view.id === report.viewId)
        if (savedView) {
          const view = { text: savedView.title ?? '', value: savedView.id ?? '' }
          setSelectedView(view)
          reportValues.view = view
        }
      }

      setScheduleRules(
        reportValues.rules?.map(rule => {
          return {
            id: crypto.randomUUID(),
            type: Number(rule.type.value) as ScheduleType
          }
        }) ?? []
      )

      reset(reportValues)
    }
  }, [report, isFetchingReport, trainingMatrixViews, gapAnalysisViews, reset])

  const onHideModal = (resetValues?: typeof defaultValues) => {
    setScheduleRules([{ id: crypto.randomUUID(), type: ScheduleType.Daily }])
    reset(resetValues)
    hide()
  }

  const onFormSubmit = (values: FieldValues) => {
    const scheduledReport: ScheduledTrainingReportDto = {
      name: values.reportName,
      subject: values.subject,
      viewId: (values.view as DropdownOption).value,
      timeZone: (values.timezone as DropdownOption).value,
      recipients: values.recipients as Array<string>,
      rules: (values.rules as Array<FieldValues>).map(rule => {
        return {
          interval: (rule.type as DropdownOption).text.toUpperCase(),
          hourOfDay: rule.hour,
          dayOfMonth: rule.day,
          month: (rule.month as DropdownOption)?.text.toUpperCase(),
          dayOfWeek: (rule.dayOfWeek as DropdownOption)?.text.toUpperCase()
        } as ScheduledTrainingReportRuleDto
      })
    }

    if (id) {
      toast.promise(
        updateReport({ id: id, scheduledTrainingReportDto: scheduledReport })
          .unwrap()
          .then(() => {
            //Timeout is needed to show the success toast before closing the modal, as the modal unmounts the component and with it the toaster
            setTimeout(() => onHideModal(defaultValues), 500)
          }),
        {
          loading: 'Updating Report...',
          success: 'Report Updated',
          error: 'Failed To Update Report'
        },
        { toasterId: scheduleExportToasterId }
      )
    } else {
      toast.promise(
        createReport({ scheduledTrainingReportDto: scheduledReport })
          .unwrap()
          .then(() => {
            //Timeout is needed to show the success toast before closing the modal, as the modal unmounts the component and with it the toaster
            setTimeout(() => onHideModal(), 500)
          }),
        {
          loading: 'Creating Report...',
          success: 'Report Created',
          error: 'Failed To Create Report'
        },
        { toasterId: scheduleExportToasterId }
      )
    }
  }

  const onSelectedReportChange = (option: DropdownOption | undefined) => {
    setSelectedView(option)
  }

  const onSelectedTimezoneChange = (option: DropdownOption | undefined) => {
    setSelectedTimezone(option)
  }

  const onScheduleRuleChange = (option: DropdownOption | undefined, index: number) => {
    if (option) {
      const rules = scheduleRules
      rules[index] = { id: rules[index].id, type: Number(option.value) as ScheduleType }

      //Reset rules when changing type, otherwise it keeps the hidden values saved
      setValue(`rules.${index}.dayOfWeek`, null)
      setValue(`rules.${index}.day`, null)
      setValue(`rules.${index}.month`, null)

      setScheduleRules([...rules])
    }
  }

  const onAddScheduleRuleClick = () =>
    setScheduleRules([...scheduleRules, { id: crypto.randomUUID(), type: ScheduleType.Daily }])

  const onRemoveRuleClick = (index: number) => {
    const rules = scheduleRules
    rules.splice(index, 1)
    setScheduleRules([...rules])

    const values = getValues()
    const currentRules = [...values.rules]
    currentRules.splice(index, 1)
    setValue('rules', currentRules)
  }

  const getScheduleRules = (type: ScheduleType, index: number) => {
    switch (type) {
      case ScheduleType.Daily:
        return <HourSchedule control={control} index={index} />
      case ScheduleType.Weekly:
        return <WeeklySchedule control={control} index={index} />
      case ScheduleType.Monthly:
        return <MonthlySchedule control={control} index={index} />
      case ScheduleType.Yearly:
        return <YearlySchedule control={control} index={index} />
      default:
        return <HourSchedule control={control} index={index} />
    }
  }

  const viewOptions = useMemo(() => {
    if (selectedPage === Page.TrainingMatrix && !isFetchingTrainingMatrixViews && trainingMatrixViews) {
      return trainingMatrixViews.map(view => {
        return { text: view.title ?? '', value: view.id ?? '' } satisfies DropdownOption
      })
    } else if (selectedPage === Page.GapAnalysis && !isFetchingGapAnalysisViews && gapAnalysisViews) {
      return gapAnalysisViews.map(view => {
        return { text: view.title ?? '', value: view.id ?? '' } satisfies DropdownOption
      })
    }

    return []
  }, [isFetchingGapAnalysisViews, isFetchingTrainingMatrixViews, trainingMatrixViews, gapAnalysisViews, selectedPage])

  const modalButtons: Array<ModalDialogButtonSetting> = [
    {
      type: 'primary',
      text: 'Save',
      name: 'save',
      form: 'schedule-export',
      onClick: () => {},
      closeModalOnClick: false
    }
  ]

  return (
    <>
      {isVisible && (
        <Dialog
          id={dialogId}
          header='Schedule Export'
          buttons={modalButtons}
          buttonType='submit'
          visible={isVisible}
          onClose={() => onHideModal()}
          containerClassName='schedule-export-dialog'>
          <form className='schedule-export' id='schedule-export' onSubmit={handleSubmit(onFormSubmit)}>
            {isFetchingReport ? (
              <ScheduleReportFormLoader />
            ) : (
              <>
                <HFSimpleInput label='Report name' control={control} name='reportName' required />
                <HFSimpleInput label='Subject' control={control} name='subject' required />
                <HFInputChips
                  name='recipients'
                  label="Recipients' Emails"
                  values={recipients}
                  onChange={values => setRecipients(values)}
                  control={control}
                  required
                />
                <HFDropdown
                  name='view'
                  label='View to Export'
                  options={viewOptions}
                  selectedOption={selectedView}
                  placeholder='Select View'
                  control={control}
                  onChange={onSelectedReportChange}
                  appendTo='self'
                  fullWidth
                  required
                />
                <HFDropdown
                  name='timezone'
                  label='Timezone'
                  options={getAllTimezones()}
                  selectedOption={selectedTimezone}
                  placeholder='Select Timezone'
                  control={control}
                  onChange={onSelectedTimezoneChange}
                  appendTo='self'
                  fullWidth
                  required
                />
              </>
            )}
            <div className='schedule-export__rule-header'>
              <h4>Schedule</h4>
              <IconButton
                icon='CirclePlus'
                className='schedule-export__action-btn'
                onClick={onAddScheduleRuleClick}
                disabled={isFetchingReport}
                aria-label='Add schedule rule'
              />
            </div>
            {isFetchingReport ? (
              <Skeleton height='2.25rem' />
            ) : (
              scheduleRules.map((rule, index) => {
                const selectedOption: DropdownOption = { text: ScheduleTypeName[rule.type], value: String(rule.type) }

                return (
                  <div key={rule.id} className='schedule-export__rule'>
                    <HFDropdown
                      name={`rules[${index}].type`}
                      label='Type'
                      options={scheduleTypeAsDropdownItems()}
                      selectedOption={selectedOption}
                      onChange={option => onScheduleRuleChange(option, index)}
                      placeholder='Select Type'
                      showClear={false}
                      control={control}
                      appendTo='self'
                      required
                    />
                    {getScheduleRules(rule.type, index)}
                    {scheduleRules.length > 1 && (
                      <IconButton
                        onClick={() => onRemoveRuleClick(index)}
                        className='schedule-export__action-btn'
                        aria-label='Remove schedule rule'
                        icon='Trash'
                      />
                    )}
                  </div>
                )
              })
            )}
            <Notifications
              appendTo={document.getElementById(dialogId) ?? document.body}
              toasterId={scheduleExportToasterId}
            />
          </form>
        </Dialog>
      )}
    </>
  )
}

export default ScheduledExport

interface ScheduleRuleProps {
  control: Control<FieldValue<FieldValues>>
  index: number
}

export function HourSchedule({ control, index }: ScheduleRuleProps) {
  return (
    <HFInputNumber
      control={control}
      name={`rules[${index}].hour`}
      value={0}
      onChange={() => {}}
      min={0}
      max={23}
      label='Hour'
      required
    />
  )
}

export function WeeklySchedule({ control, index }: ScheduleRuleProps) {
  const [selectedWeek, setSelectedWeek] = useState<DropdownOption>()

  const onWeekChange = (option: DropdownOption | undefined) => {
    setSelectedWeek(option)
  }

  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      <HFDropdown
        name={`rules[${index}].dayOfWeek`}
        control={control}
        label='Day of Week'
        placeholder='Select day of week'
        options={dayOfWeekEnumAsDropdownItems()}
        selectedOption={selectedWeek}
        onChange={onWeekChange}
        appendTo='self'
        required
      />
      <HFInputNumber
        name={`rules[${index}].hour`}
        control={control}
        value={0}
        onChange={() => {}}
        min={0}
        max={23}
        label='Hour'
        required
      />
    </div>
  )
}

export function MonthlySchedule({ control, index }: ScheduleRuleProps) {
  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      <HFInputNumber
        control={control}
        name={`rules[${index}].day`}
        value={1}
        onChange={() => {}}
        min={1}
        max={28}
        label='Day'
        required
      />
      <HFInputNumber
        control={control}
        name={`rules[${index}].hour`}
        value={0}
        onChange={() => {}}
        min={0}
        max={23}
        label='Hour'
        required
      />
    </div>
  )
}

export function YearlySchedule({ control, index }: ScheduleRuleProps) {
  const [selectedMonth, setSelectedMonth] = useState<DropdownOption>()

  const onMonthChange = (option: DropdownOption | undefined) => {
    setSelectedMonth(option)
  }

  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      <HFDropdown
        name={`rules[${index}].month`}
        control={control}
        label='Month'
        placeholder='Select Month'
        options={monthEnumAsDropdownItems()}
        selectedOption={selectedMonth}
        onChange={onMonthChange}
        appendTo='self'
        required
      />
      <HFInputNumber
        control={control}
        name={`rules[${index}].day`}
        value={1}
        onChange={() => {}}
        min={1}
        max={28}
        label='Day'
        required
      />
      <HFInputNumber
        control={control}
        name={`rules[${index}].hour`}
        value={0}
        onChange={() => {}}
        min={0}
        max={23}
        label='Hour'
        required
      />
    </div>
  )
}

function ScheduleReportFormLoader() {
  return (
    <>
      <div className='field'>
        <Skeleton height='2.25rem' />
      </div>
      <div className='field'>
        <Skeleton height='2.25rem' />
      </div>
      <div className='field'>
        <Skeleton height='3.75rem' />
      </div>
      <div className='field'>
        <Skeleton height='2.25rem' />
      </div>
      <div className='field'>
        <Skeleton height='2.25rem' />
      </div>
    </>
  )
}
